import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthProvider';
import { useDealership } from '../../contexts/DealershipContext';
import { useCart } from '../../contexts/CartContext';
import UserContext from '../../contexts/UserContext';
import { TEMPLATES } from './templateImageMap';
import './BuildYourAdPage.css';

const BuildYourAdPage = () => {
  const { api } = useAuth();
  const history = useHistory();
  const { user } = React.useContext(UserContext);
  const { dealership } = useDealership();
  const { addToCart, updateOrderDetails } = useCart();

  const [adType, setAdType] = useState('');
  const [adSize, setAdSize] = useState('');
  const [numVehicles, setNumVehicles] = useState('');
  const [offerTypes, setOfferTypes] = useState([]);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [allProducts, setAllProducts] = useState([]);
  const [customAdItem, setCustomAdItem] = useState(null);
  const [touchedTemplateId, setTouchedTemplateId] = useState(null);

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await api.get('/api/getAllItems');

        if (response.data && response.data.items) {
          const items = response.data.items;
          const customItem = items.find(item => item.name === 'CustomAdRequest');
          setCustomAdItem(customItem || null);
          const regularItems = items.filter(item => item.name !== 'CustomAdRequest');
          setAllProducts(regularItems);

          setFilteredProducts(regularItems);
        } else {
          setError("No products found");
        }
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Failed to load products. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [api]);

  useEffect(() => {
    const count = parseInt(numVehicles, 10) || 0;
    setOfferTypes(prev => {
      const next = Array.from({ length: count }, (_, i) => prev[i] ?? '');
      return next;
    });
  }, [numVehicles]);

  useEffect(() => {
    if (allProducts.length === 0) return;

    let filtered = [...allProducts];

    if (adType) {
      filtered = filtered.filter(item =>
        item.creativeType && item.creativeType.name?.toLowerCase() === adType.toLowerCase()
      );
    }
    if (adSize) {
      filtered = filtered.filter(item => item.size?.toLowerCase() === adSize.toLowerCase());
    }
    if (numVehicles) {
      filtered = filtered.filter(item => String(item.numVehicles) === numVehicles);
    }

    setFilteredProducts(filtered);
  }, [adType, adSize, numVehicles, allProducts]);

  const handleAddToCart = (e) => {
    e.preventDefault();
    if (!customAdItem) {
      console.error('CustomAdRequest item not loaded');
      return;
    }

    const adRequestItem = {
      itemId: customAdItem.id,
      name: customAdItem.name || 'Custom Ad Request',
      image: customAdItem.image,
      quantity: '1',
      price: String(customAdItem.price || 0),
      fee: String(customAdItem.fee || 0),
      markup: '0',
      total: '0',
      adType: adType || '',
      adSize: adSize || '',
      numVehicles: numVehicles || '',
      offerType: JSON.stringify(
        offerTypes.map((offer, i) => ({ vehicle: i + 1, offer }))
      ),
      notes: notes || ''
    };

    addToCart(adRequestItem);
    updateOrderDetails({
      name: dealership?.name || '',
      email: user?.email || '',
      phone: dealership?.salesPhone || '',
      status: 'DRAFT'
    });
    history.push('/cart');
  };

  const handleReset = () => {
    setAdType('');
    setAdSize('');
    setNumVehicles('');
    setOfferTypes([]);
    setNotes('');
  };

  const handleTemplateTouch = (productId) => {
    setTouchedTemplateId(prev => prev === productId ? null : productId);
  };

  const vehicleCount = parseInt(numVehicles, 10) || 0;
  const isFormComplete =
    adType &&
    adSize &&
    vehicleCount > 0 &&
    offerTypes.length === vehicleCount &&
    offerTypes.every(ot => ot.trim() !== '') &&
    customAdItem;

  if (loading) return <div className="loading">Loading ad options...</div>;
  if (error) return <div className="error">{error}</div>;

  return (
    <div className="build-your-ad-container">
      <h1>Build Your Ad</h1>
      <div className="build-your-ad-layout">

        {/* Left: Form */}
        <div className="ad-form-section">
          <form className="ad-form" onSubmit={handleAddToCart}>
            <div className="form-group">
              <label>Ad Type</label>
              <div className="ad-type-toggle">
                <button
                  type="button"
                  className={`ad-type-btn${adType === 'Print' ? ' active' : ''}`}
                  onClick={() => setAdType(adType === 'Print' ? '' : 'Print')}
                >
                  Print
                </button>
                <button
                  type="button"
                  className={`ad-type-btn${adType === 'Digital' ? ' active' : ''}`}
                  onClick={() => setAdType(adType === 'Digital' ? '' : 'Digital')}
                >
                  Digital
                </button>
                <button
                  type="button"
                  className={`ad-type-btn${adType === 'Digital/Email' ? ' active' : ''}`}
                  onClick={() => setAdType(adType === 'Digital/Email' ? '' : 'Digital/Email')}
                >
                  Digital/Email
                </button>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="ad-size">Ad Size</label>
              <input
                id="ad-size"
                type="text"
                className="plain-input"
                placeholder="Enter Ad Size"
                value={adSize}
                onChange={(e) => setAdSize(e.target.value)}
                autoComplete="off"
              />
            </div>

            <div className="form-group">
              <label htmlFor="num-vehicles">Number of Vehicles</label>
              <input
                id="num-vehicles"
                type="text"
                className="plain-input"
                placeholder="Enter Number of Vehicles"
                value={numVehicles}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/, '');
                  if (val === '' || parseInt(val, 10) <= 16) setNumVehicles(val);
                }}
                autoComplete="off"
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </div>

            <div className="form-group">
              <label>Offer Type</label>
              {vehicleCount === 0 ? (
                <p className="offer-type-hint">Enter number of vehicles above to set offer types.</p>
              ) : (
                <div className="offer-types-list">
                  {offerTypes.map((offer, i) => (
                    <div key={i} className="offer-type-row">
                      <span className="offer-type-vehicle-label">Vehicle {i + 1}</span>
                      <input
                        type="text"
                        className="plain-input"
                        placeholder={`Offer for vehicle ${i + 1}`}
                        value={offer}
                        onChange={(e) => {
                          const val = e.target.value;
                          setOfferTypes(prev => prev.map((o, j) => j === i ? val : o));
                        }}
                        autoComplete="off"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="notes">Notes</label>
              <textarea
                id="notes"
                className="notes-textarea"
                placeholder="Add notes for this ad..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={4}
              />
            </div>

            <div className="form-actions">
              <button className="reset-button" type="button" onClick={handleReset}>
                Reset
              </button>
              <button
                className="add-to-cart-button"
                type="submit"
                disabled={!isFormComplete}
              >
                Add to Cart
              </button>
            </div>
          </form>
        </div>

        {/* Right: Templates */}
        <div className="templates-section">
          <h2 className="templates-heading">Sample Templates</h2>
          <div className="templates-grid">
            {TEMPLATES.map(tmpl => (
              <div
                key={tmpl.id}
                className={`template-card${touchedTemplateId === tmpl.id ? ' touched' : ''}`}
                onClick={() => handleTemplateTouch(tmpl.id)}
              >
                <div className="template-thumbnail">
                  <img
                    src={tmpl.base}
                    alt={tmpl.name}
                    loading="lazy"
                  />
                </div>

                <div className="template-overlay">
                  <img
                    src={tmpl.final}
                    alt={`${tmpl.name} finalized`}
                  />
                  <div className="overlay-label">Sample Transformation</div>
                  <span className="overlay-close-hint">✕</span>
                </div>

                <div className="template-info">
                  <span className="template-name">{tmpl.name}</span>
                  <span className="template-size">{tmpl.type} · {tmpl.size}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  );
};

export default BuildYourAdPage;
